import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { UseFieldConfig } from 'react-final-form'
import { Button, Chips } from '@cargomart/ui'
import { MODAL_IDS, ModalItem, ModalSimple, useModal } from 'components'
import { LinkButton } from 'components/extend'
import { FormApi } from 'final-form'
import { useLazyGetParamTerminalOperationsQuery, useLazyGetPointOperationsQuery } from 'modules/order/api'
import IconPlus from 'remixicon/icons/System/add-line.svg?react'

import { OrderCreatePointOperationContent } from './OrderCreatePointOperationContent'
import { OrderCreateOperationForm } from './types'

interface IProps extends Pick<UseFieldConfig<OrderCreateOperationForm[]>, 'validate'> {
  onChange: (data: OrderCreateOperationForm[]) => void
  pointType: 'departure' | 'destination'
  value: OrderCreateOperationForm[]
  terminalId?: string
}

export const OrderCreatePointOperation = memo<IProps>(function OrderCreatePointOperation({
  pointType,
  terminalId,
  value,
  onChange
}) {
  const modalId =
    pointType === 'departure'
      ? MODAL_IDS.OrderCreateOperationPointDeparture
      : MODAL_IDS.OrderCreateOperationPointDestination
  const { openModal, closeModal } = useModal()
  const formRef = useRef<FormApi<{ amounts: Record<string, number | null> }> | null>(null)

  const [initialValues, setInitialValues] = useState<{ amounts: Record<string, number> }>({ amounts: {} })
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [getPointOperation, { data: pointOperations = [], isLoading, isFetching }] = useLazyGetPointOperationsQuery()
  const [
    getParamTerminalOperations,
    { data: terminalOperationParams = [], isLoading: isParamLoading, isFetching: isParamFetching }
  ] = useLazyGetParamTerminalOperationsQuery()

  const operationNameMap = useMemo(() => {
    const map: Record<string, string> = {}

    pointOperations.forEach((op) => {
      map[op.id] = op.name
    })

    return map
  }, [pointOperations])

  const maxAmountByOperationId = useMemo(() => {
    const map: Record<string, number> = {}

    pointOperations.forEach((op) => {
      const param = terminalOperationParams.find((item) => item.operationId === op.id)
      map[op.id] = param && param.maxAmount > 1 ? param.maxAmount : 1
    })

    return map
  }, [pointOperations, terminalOperationParams])

  const handleSubmit = useCallback(
    (values: { amounts: Record<string, number | null> }) => {
      const currentValue = value || []
      const otherOperations = currentValue.filter((op) => op.terminalId !== terminalId || op.pointType !== pointType)
      const operations: OrderCreateOperationForm[] = []

      Object.entries(values.amounts).forEach(([operationId, amount]) => {
        if (typeof amount !== 'number' || amount <= 0) return

        operations.push({
          operationId,
          terminalId,
          pointType,
          name: operationNameMap[operationId] ?? '',
          amount
        })
      })

      onChange([...otherOperations, ...operations])
      closeModal(modalId)
    },
    [closeModal, modalId, onChange, operationNameMap, pointType, terminalId, value]
  )

  const handleRemoveOperation = useCallback(
    (operationId: string) => {
      const currentValue = value || []
      const newValue = currentValue.filter(
        (op) => !(op.operationId === operationId && op.terminalId === terminalId && op.pointType === pointType)
      )
      onChange(newValue)
    },
    [value, onChange, pointType, terminalId]
  )

  const handleOpenModal = useCallback(() => {
    if (!terminalId) return

    const currentValue = value || []
    const amounts: Record<string, number> = {}
    currentValue.forEach((op) => {
      if (op.terminalId !== terminalId || op.pointType !== pointType) return

      amounts[op.operationId] = op.amount
    })

    setInitialValues({ amounts })
    getPointOperation({ type: pointType })
    getParamTerminalOperations({ id: terminalId, type: pointType })
    openModal(modalId)
  }, [terminalId, value, getPointOperation, getParamTerminalOperations, pointType, openModal, modalId])

  const handleApply = useCallback(() => {
    formRef.current?.submit()
  }, [])

  const handleValidityChange = useCallback((isValid: boolean) => {
    setIsSubmitDisabled(!isValid)
  }, [])

  const handleCancel = useCallback(() => {
    closeModal(modalId)
  }, [closeModal, modalId])

  const displayLabel = useMemo(
    () => (pointType === 'departure' ? 'Добавление услуги при отправлении' : 'Добавление услуги при получении'),
    [pointType]
  )

  const selectedOperationsWithNames = useMemo(() => {
    if (!terminalId) return []
    const currentValue = value || []
    const selected = currentValue?.filter((op) => op.terminalId === terminalId && op.pointType === pointType) || []

    return selected
      .map((op) => ({
        operationId: op.operationId,
        name: op.name,
        terminalId: op.terminalId,
        amount: op.amount
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }, [pointType, terminalId, value])

  return (
    <>
      {selectedOperationsWithNames.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {selectedOperationsWithNames.map((op) => (
            <Chips
              key={`${op.terminalId}-${pointType}-${op.operationId}`}
              informative
              onClear={() => handleRemoveOperation(op.operationId)}
              size="sm"
              value={op.amount > 1 ? `${op.name}, ${op.amount} шт` : op.name}
            />
          ))}
        </div>
      )}

      <LinkButton
        className="link flex h-[31px] select-none items-center  !text-on-surface-link"
        iconLeft={<IconPlus className="mr-2" containerSize="xs" size="xs" />}
        onClick={handleOpenModal}
      >
        Добавить дополнительную услугу
      </LinkButton>

      <ModalItem id={modalId}>
        <ModalSimple
          hasHeader
          isStickyFooter
          isStickyHeader
          bodyClassName="bg-surface"
          footerClassName="bg-surface"
          id={`${pointType}-operations-modal`}
          isCloseOnOutsideClick={false}
          onClose={handleCancel}
          title={displayLabel}
          mobileModalFooter={
            <Button
              className="block w-full"
              disabled={isSubmitDisabled}
              onClick={handleApply}
              size="lg"
              variant="solid"
            >
              Применить
            </Button>
          }
          modalFooter={
            <div className="flex justify-end gap-3">
              <Button onClick={handleCancel} size="lg" variant="outline">
                Отменить
              </Button>
              <Button disabled={isSubmitDisabled} onClick={handleApply} size="lg" variant="solid">
                Применить
              </Button>
            </div>
          }
        >
          <OrderCreatePointOperationContent
            formRef={formRef}
            initialValues={initialValues}
            isFetching={isFetching || isParamFetching}
            isLoading={isLoading || isParamLoading}
            maxAmountByOperationId={maxAmountByOperationId}
            onSubmit={handleSubmit}
            onValidityChange={handleValidityChange}
            pointOperations={pointOperations}
          />
        </ModalSimple>
      </ModalItem>
    </>
  )
})
